import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Card from '@mui/material/Card'
import { CardContent, createTheme } from '@mui/material'
import CardHeader from '@mui/material/CardHeader'
import CardMedia from '@mui/material/CardMedia'
import CardActionArea from '@mui/material/CardActionArea'
import Stack from '@mui/material/Stack'
import { ThemeProvider } from '@mui/material/styles'

import { useContextHook } from '../store/context'
import { Machine } from '../types/machine'
import { setPageTitle } from '../utils/service'
import MachineLoader from '../components/navbar/machineLoader'

const P2000IMAGE = 'p2000.png'
const L100IMAGE = 'P16_L100_front.png'

const themeCardActionArea = createTheme({
  components: {
    MuiCardActionArea: {
      styleOverrides: {
        focusHighlight: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        subheader: {
          color: '#FAFAFA',
        },
      },
    },
  },
})

const MainView = (): JSX.Element => {
  const { t } = useTranslation()
  const { state } = useContextHook()

  const getMachineImage = (machine: Machine): { imageUrl: string; altText: string } => {
    const isDarkTheme = state.user?.is_darkmode

    // TODO: add dark mode images
    if (machine.machine_image === P2000IMAGE) {
      return { imageUrl: isDarkTheme ? '/machine_P2000.png' : '/machine_P2000.png', altText: 'mainview.p2000alt' }
    } else if (machine.machine_image === L100IMAGE) {
      return { imageUrl: isDarkTheme ? '/machine_L100.png' : '/machine_L100.png', altText: 'mainview.l100alt' }
    } else {
      return { imageUrl: isDarkTheme ? '/machine_S100.png' : '/machine_S100.png', altText: 'mainview.s100alt' }
    }
  }

  const renderLinkButton = (isSmall: boolean, url: string, label: string): JSX.Element => {
    return (
      <Button variant="outlined" sx={{ m: '0.1rem' }} size={isSmall ? 'small' : 'large'} component={Link} to={url}>
        {t(label)}
      </Button>
    )
  }

  // update page title
  React.useEffect(() => {
    setPageTitle('')
  }, [])

  return (
    <Container component="main" maxWidth={false}>
      <Stack direction="row" flexWrap="wrap">
        {(state.machines || []).map((machine) => {
          return (
            <Card
              key={machine.id}
              sx={{
                mb: 4,
                mr: { xs: 0, sm: 4 },
                '&:hover': { backgroundColor: 'secondary.light' },
              }}
            >
              <ThemeProvider theme={themeCardActionArea}>
                <CardActionArea component={Link} to={`/machine/${machine?.slug}/live/`}>
                  <CardHeader
                    title={machine.name}
                    subheader={
                      machine.prod_batch && machine.prod_number
                        ? `p${machine.prod_batch}_${machine.prod_number}`
                        : 'N/A'
                    }
                  />
                  <CardMedia
                    sx={{
                      maxWidth: state.machines.length === 1 ? 500 : { xs: undefined, sm: 240 },
                      alignSelf: 'center',
                    }}
                    component="img"
                    image={getMachineImage(machine).imageUrl}
                    alt={t(getMachineImage(machine).altText)}
                  />
                </CardActionArea>
              </ThemeProvider>
              <CardContent>
                {renderLinkButton(state.machines.length > 1, `/machine/${machine?.slug}/live/`, 'navmenu.live')}
                {renderLinkButton(state.machines.length > 1, `/machine/${machine?.slug}/logs/`, 'navmenu.logs')}
                {renderLinkButton(state.machines.length > 1, `/machine/${machine?.slug}/profiles/`, 'navmenu.profiles')}
              </CardContent>
            </Card>
          )
        })}
        <MachineLoader />
      </Stack>
    </Container>
  )
}

export default MainView
